import { useRef } from "react";
import { motion, useInView } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export function FadeUp({ children, delay = 0, className = "", y = 28, ...rest }) {
  return (
    <motion.div
      className={className}
      {...rest}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/* Line-by-line masked text reveal. The observer watches the OUTER span
   (unclipped, untransformed) — the inner span can safely sit translated
   at 115% inside the overflow-hidden mask until the line enters view. */
export function InviewMaskLine({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <span ref={ref} className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={inView ? { y: "0%" } : { y: "115%" }}
        transition={{ duration: 1.0, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/* Masked image reveal — observer on the outer frame, animation on the img. */
export function RevealImage({ src, alt, className = "", imgClassName = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <div ref={ref} className={`img-frame ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        className={imgClassName}
        initial={{ clipPath: "inset(100% 0% 0% 0%)", scale: 1.12 }}
        animate={inView ? { clipPath: "inset(0% 0% 0% 0%)", scale: 1 } : { clipPath: "inset(100% 0% 0% 0%)", scale: 1.12 }}
        transition={{ clipPath: { duration: 1.2, ease: EASE }, scale: { duration: 1.6, ease: EASE } }}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

/* Hero-only variant: animates on mount. */
export function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.0, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}
